import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlanificacion } from '@context/PlanificacionContext';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { BookOpen, ArrowLeft, ArrowRight, Info, CheckCircle, Sparkles, Loader2, Wand2 } from 'lucide-react'; 
import Button from '@components/common/Button';

const UnidadesPage = () => {
  const navigate = useNavigate();
  const { 
    area,
    grado,
    periodos, 
    calendario, 
    unidades, 
    agregarUnidad, 
    actualizarUnidad,
    getSesionDeSemana,
    semanasVacaciones,
    semanasGestion
  } = usePlanificacion();

  const [distribucion, setDistribucion] = useState({});
  const [activeTab, setActiveTab] = useState(periodos[0]?.id || null);
  const [loadingIA, setLoadingIA] = useState({});
  const [loadingGlobalIA, setLoadingGlobalIA] = useState(false);
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  useEffect(() => {
    if (periodos.length > 0 && Object.keys(distribucion).length === 0) {
      const initDist = {};
      periodos.forEach(p => initDist[p.id] = 2);
      setDistribucion(initDist);
      setActiveTab(periodos[0].id);
    }
  }, [periodos]);

  useEffect(() => {
    if (periodos.length === 0) navigate('/periodos');
  }, [periodos, navigate]);

  const getSemanasPeriodo = (periodoId) => {
    const periodo = periodos.find(p => p.id === periodoId);
    if (!periodo) return [];
    const idxInicio = calendario.findIndex(s => s.id === periodo.semana_inicio_id);
    const idxFin = calendario.findIndex(s => s.id === periodo.semana_fin_id);
    return calendario.slice(idxInicio, idxFin + 1);
  };

  const esSemanaValida = (semanaId) => !semanasVacaciones.includes(semanaId) && !semanasGestion.includes(semanaId);

  const handleGenerarDistribucion = () => {
    let idTemporal = 1;

    periodos.forEach((periodo) => {
      const unidadesDeseadas = distribucion[periodo.id] || 0;
      if (unidadesDeseadas === 0) return;

      const semanasDelPeriodo = getSemanasPeriodo(periodo.id).filter(s => esSemanaValida(s.id));
      const baseSemanasPorUnidad = Math.floor(semanasDelPeriodo.length / unidadesDeseadas);
      let restoSemanas = semanasDelPeriodo.length % unidadesDeseadas;

      let currentIndex = 0;

      for (let i = 1; i <= unidadesDeseadas; i++) {
        let semanasAsignadas = baseSemanasPorUnidad + (restoSemanas > 0 ? 1 : 0);
        if (restoSemanas > 0) restoSemanas--;

        const bloque = semanasDelPeriodo.slice(currentIndex, currentIndex + semanasAsignadas);
        
        if (bloque.length > 0) {
          agregarUnidad({
            id: idTemporal,
            periodo_id: periodo.id,
            numero_unidad: idTemporal,
            titulo: `Unidad ${idTemporal}`,
            situacion_significativa: '',
            producto_final: '',
            semana_inicio_id: bloque[0].id,
            semana_fin_id: bloque[bloque.length - 1].id
          });
          idTemporal++;
        }
        currentIndex += semanasAsignadas;
      }
    });
  };

  const formatearFecha = (fechaString) => {
    if (!fechaString) return '';
    return format(parseISO(fechaString), "dd MMM", { locale: es });
  };

  const handleCambiarLimite = (unidadId, campo, nuevoSemanaIdStr) => {
    const nuevoSemanaId = parseInt(nuevoSemanaIdStr);
    const unidadActual = unidades.find(u => u.id === unidadId);
    const unidadesDelPeriodo = unidades.filter(u => u.periodo_id === unidadActual.periodo_id).sort((a,b) => a.id - b.id);
    const indexEnPeriodo = unidadesDelPeriodo.findIndex(u => u.id === unidadId);
    
    actualizarUnidad(unidadActual.id, { [campo]: nuevoSemanaId });
    
    if (campo === 'semana_fin_id' && indexEnPeriodo < unidadesDelPeriodo.length - 1) {
      const idxNueva = calendario.findIndex(s => s.id === nuevoSemanaId);
      let nextValid = calendario.slice(idxNueva + 1).find(s => esSemanaValida(s.id));
      if (nextValid) actualizarUnidad(unidadesDelPeriodo[indexEnPeriodo + 1].id, { semana_inicio_id: nextValid.id });
    }
    if (campo === 'semana_inicio_id' && indexEnPeriodo > 0) {
      const idxNueva = calendario.findIndex(s => s.id === nuevoSemanaId);
      let prevValid = [...calendario].slice(0, idxNueva).reverse().find(s => esSemanaValida(s.id));
      if (prevValid) actualizarUnidad(unidadesDelPeriodo[indexEnPeriodo - 1].id, { semana_fin_id: prevValid.id });
    }
  };

  const generarUnaUnidad = async (unidad, sesionesDeEstaUnidad) => {
    const titulosSesiones = sesionesDeEstaUnidad.map(s => s.titulo);
    
    if (titulosSesiones.length === 0) return null;

    setLoadingIA(prev => ({ ...prev, [unidad.id]: true }));

    try {
      const API_URL = import.meta.env.VITE_API_BASE_URL.replace('/api/v1', '');
      const API_PREFIX = '/api/v1';

      const response = await fetch(`${API_URL}${API_PREFIX}/ia/generar-unidad`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grado: grado.nombre || grado, 
          area: area.nombre || area,
          numero_unidad: unidad.numero_unidad,
          sesiones: titulosSesiones
        })
      });

      if (!response.ok) throw new Error('Error en el servidor');

      const data = await response.json(); 

      actualizarUnidad(unidad.id, {
        titulo: data.titulo,
        situacion_significativa: data.situacion_significativa,
        producto_final: data.producto_final
      });
      return true;
    } catch (error) {
      console.error(`Error unidad ${unidad.id}:`, error);
      return false;
    } finally {
      setLoadingIA(prev => ({ ...prev, [unidad.id]: false }));
    }
  };

  const handleGenerarConIA = async (unidad, sesionesDeEstaUnidad) => {
    if (!area || !grado) return alert("Faltan datos de Área o Grado.");
    if (sesionesDeEstaUnidad.length === 0) return alert("Esta unidad no tiene sesiones asignadas para usar como base.");
    await generarUnaUnidad(unidad, sesionesDeEstaUnidad);
  };

  const handleGenerarTodoElPeriodo = async () => {
    if (!area || !grado) return alert("Faltan datos de Área o Grado.");
    
    setLoadingGlobalIA(true);

    const unidadesDelPeriodo = unidades.filter(u => u.periodo_id === activeTab);
    const semanasPeriodo = getSemanasPeriodo(activeTab);

    for (const unidad of unidadesDelPeriodo) {
      const idxIni = semanasPeriodo.findIndex(s => s.id === unidad.semana_inicio_id);
      const idxFin = semanasPeriodo.findIndex(s => s.id === unidad.semana_fin_id);
      
      let sesionesUnidad = [];
      if (idxIni >= 0 && idxFin >= 0) {
        semanasPeriodo.slice(idxIni, idxFin + 1).forEach(s => {
          const ses = getSesionDeSemana(s.id);
          if (ses) sesionesUnidad.push({ semana: s.numero_semana, titulo: ses.titulo });
        });
      }

      if (sesionesUnidad.length > 0) {
        await generarUnaUnidad(unidad, sesionesUnidad);
        await delay(500);
      }
    }

    setLoadingGlobalIA(false);
  };

  if (periodos.length === 0) return null;

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-8 pb-4 border-b">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-blue-600" /> Creación de Unidades Didácticas
          </h1>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/periodos')} className="flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Volver</Button>
          {unidades.length > 0 && <Button onClick={() => navigate('/configuracion-final')} className="bg-blue-600 text-white flex items-center gap-2">Siguiente Paso <ArrowRight className="w-4 h-4" /></Button>}
        </div>
      </div>

      {unidades.length === 0 ? (
        <div className="max-w-2xl mx-auto mt-8 bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
          <h2 className="text-xl font-bold text-gray-800 mb-2 text-center">Configura tus unidades por periodo</h2>
          <p className="text-gray-500 text-center mb-8 text-sm">Elige cuántas unidades o proyectos trabajarás en cada bloque. Luego el sistema las distribuirá en el tiempo disponible.</p>
          
          <div className="space-y-4 mb-8">
            {periodos.map(periodo => (
              <div key={periodo.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border">
                <div>
                  <h3 className="font-bold text-gray-800">{periodo.nombre}</h3>
                  <p className="text-xs text-gray-500">
                    Semanas válidas: {getSemanasPeriodo(periodo.id).filter(s => esSemanaValida(s.id)).length}
                  </p>
                </div>
                
                <div className="flex items-center gap-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setDistribucion(prev => ({...prev, [periodo.id]: Math.max(0, prev[periodo.id] - 1)}))}
                    className="w-10 h-10 rounded-full"
                  >-</Button>
                  <span className="text-2xl font-black text-blue-600 w-8 text-center">{distribucion[periodo.id]}</span>
                  <Button 
                    variant="outline" 
                    onClick={() => setDistribucion(prev => ({...prev, [periodo.id]: prev[periodo.id] + 1}))}
                    className="w-10 h-10 rounded-full"
                  >+</Button>
                </div>
              </div>
            ))}
          </div>

          <Button onClick={handleGenerarDistribucion} className="w-full bg-blue-600 text-white py-3 text-lg shadow-md hover:shadow-lg transition-all">
            Generar Distribución Inicial
          </Button>
        </div>
      ) : (
        <div>
          {/* ✨ BANNER MÁGICO DE ONBOARDING */}
          <div className="mb-6 bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-100 rounded-xl p-4 flex items-start gap-4 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-gradient-to-br from-violet-200 to-fuchsia-200 rounded-full blur-2xl opacity-50 pointer-events-none"></div>
            
            <div className="bg-white p-2 rounded-lg shadow-sm border border-violet-100 text-violet-600 flex-shrink-0 z-10">
              <Sparkles className="w-6 h-6 animate-pulse" />
            </div>
            <div className="z-10">
              <h4 className="font-bold text-violet-900">¡Ahorra horas de trabajo con IA!</h4>
              <p className="text-violet-700 text-sm mt-1 leading-relaxed">
                No empieces desde cero. Usa la varita mágica para redactar sugerencias de <strong>títulos, situaciones significativas y productos</strong> basándose en tus sesiones asignadas. Podrás modificar o borrar libremente el resultado.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between mb-6 border-b pb-2">
            <div className="flex space-x-2 overflow-x-auto">
              {periodos.map(periodo => {
                const cantUnidades = unidades.filter(u => u.periodo_id === periodo.id).length;
                return (
                  <button
                    key={periodo.id}
                    onClick={() => setActiveTab(periodo.id)}
                    className={`px-6 py-3 rounded-t-lg font-semibold transition-colors flex items-center gap-2 whitespace-nowrap ${
                      activeTab === periodo.id ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {periodo.nombre}
                    <span className={`px-2 py-0.5 rounded-full text-xs ${activeTab === periodo.id ? 'bg-blue-500 text-white' : 'bg-gray-300'}`}>
                      {cantUnidades} U
                    </span>
                  </button>
                );
              })}
            </div>
            
            {/* ✨ BOTÓN MAESTRO MEJORADO */}
            <Button 
              onClick={handleGenerarTodoElPeriodo}
              disabled={loadingGlobalIA}
              className={`flex items-center gap-2 text-sm font-bold px-6 py-2.5 ${
                loadingGlobalIA 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-gradient-to-r from-violet-600 via-fuchsia-600 to-indigo-600 hover:shadow-lg hover:shadow-violet-500/30 hover:scale-[1.02]'
              } text-white rounded-xl shadow-md transition-all duration-300 border-none`}
            >
              {loadingGlobalIA ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />}
              {loadingGlobalIA ? 'Analizando sesiones...' : '✨ Autocompletar Periodo'}
            </Button>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {unidades.filter(u => u.periodo_id === activeTab).sort((a,b) => a.id - b.id).map((unidad, index) => {
              const semanasPeriodo = getSemanasPeriodo(activeTab);
              const idxIni = semanasPeriodo.findIndex(s => s.id === unidad.semana_inicio_id);
              const idxFin = semanasPeriodo.findIndex(s => s.id === unidad.semana_fin_id);
              
              let sesionesUnidad = [];
              if (idxIni >= 0 && idxFin >= 0) {
                semanasPeriodo.slice(idxIni, idxFin + 1).forEach(s => {
                  const ses = getSesionDeSemana(s.id);
                  if (ses) sesionesUnidad.push({ semana: s.numero_semana, titulo: ses.titulo });
                });
              }

              return (
                <div key={unidad.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  
                  <div className="bg-blue-50 border-b p-3 flex justify-between items-center gap-2">
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-blue-900 uppercase text-sm">Unidad {unidad.numero_unidad}</span>
                      
                      {/* ✨ ESTADO DE CARGA INDIVIDUAL MEJORADO */}
                      {loadingIA[unidad.id] ? (
                        <div className="flex items-center gap-1.5 text-xs font-bold bg-violet-100 text-violet-700 px-3 py-1.5 rounded-full shadow-inner border border-violet-200 animate-pulse">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Completando unidad...
                        </div>
                      ) : (
                        <button 
                          onClick={() => handleGenerarConIA(unidad, sesionesUnidad)}
                          disabled={loadingGlobalIA}
                          className={`flex items-center gap-1.5 text-xs font-semibold bg-white px-3 py-1.5 rounded-full shadow-sm border transition-all ${loadingGlobalIA ? 'opacity-50 cursor-not-allowed text-gray-400' : 'text-blue-700 hover:bg-blue-50 hover:border-blue-300 active:scale-95'}`}
                          title="Generar título, situación y producto basado en las sesiones"
                        >
                          <Sparkles className={`w-3.5 h-3.5 ${loadingGlobalIA ? 'text-gray-400' : 'text-violet-500'}`} />
                          Sugerir
                        </button>
                      )}
                    </div>

                    <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded font-semibold whitespace-nowrap">
                      {idxFin >= 0 && idxIni >= 0 ? idxFin - idxIni + 1 : 0} semanas
                    </span>
                  </div>

                  <div className="p-5 space-y-5">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Título de la Unidad / Proyecto</label>
                      <input 
                        type="text" value={unidad.titulo} onChange={(e) => actualizarUnidad(unidad.id, { titulo: e.target.value })}
                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none font-medium text-gray-800"
                        placeholder="Ej. Conocemos nuestra historia local..."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Inicia:</label>
                        <select 
                          value={unidad.semana_inicio_id || ''} onChange={(e) => handleCambiarLimite(unidad.id, 'semana_inicio_id', e.target.value)}
                          className="w-full p-2 border rounded text-sm bg-gray-50 outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {semanasPeriodo.map(sem => (
                            <option key={sem.id} value={sem.id} disabled={!esSemanaValida(sem.id)}>
                              Sem {sem.numero_semana} ({formatearFecha(sem.fecha_inicio)})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Termina:</label>
                        <select 
                          value={unidad.semana_fin_id || ''} onChange={(e) => handleCambiarLimite(unidad.id, 'semana_fin_id', e.target.value)}
                          className="w-full p-2 border rounded text-sm bg-gray-50 outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {semanasPeriodo.map(sem => (
                            <option key={sem.id} value={sem.id} disabled={!esSemanaValida(sem.id)}>
                              Sem {sem.numero_semana} ({formatearFecha(sem.fecha_fin)})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Situación Significativa</label>
                      <textarea 
                        value={unidad.situacion_significativa || ''} onChange={(e) => actualizarUnidad(unidad.id, { situacion_significativa: e.target.value })}
                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none text-sm resize-none h-20 text-gray-700"
                        placeholder="Describe el contexto o problema que detona esta unidad..."
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Producto Final</label>
                      <input 
                        type="text" 
                        value={unidad.producto_final || ''}
                        onChange={(e) => actualizarUnidad(unidad.id, { producto_final: e.target.value })}
                        placeholder="Ej. Elaboración de un ensayo argumentativo"
                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none text-sm text-gray-800"
                      />
                    </div>

                    <div className="mt-4 bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <p className="text-xs font-bold text-gray-500 uppercase mb-2">Contenido detectado ({sesionesUnidad.length} sesiones)</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {sesionesUnidad.slice(0, 3).map((ses, i) => (
                          <li key={i} className="flex items-center gap-2 truncate">
                            <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                            <span className="font-semibold">S{ses.semana}:</span> {ses.titulo}
                          </li>
                        ))}
                        {sesionesUnidad.length > 3 && (
                          <li className="text-xs text-blue-600 font-medium pl-5">+ {sesionesUnidad.length - 3} sesiones más...</li>
                        )}
                        {sesionesUnidad.length === 0 && (
                          <li className="text-xs text-yellow-600 flex items-center gap-1">
                            <Info className="w-3 h-3" /> No hay sesiones arrastradas en estas semanas.
                          </li>
                        )}
                      </ul>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnidadesPage;
